<template>
  <div class="document-types-panel">
    <div class="panel-header">
      <span class="panel-title">{{ $tc('tournament.document_types') || 'Document Types' }}</span>
      <el-button size="small" type="primary" icon="el-icon-plus" @click="openAddModal">
        {{ $tc('general.add') }}
      </el-button>
    </div>

    <div v-if="loading" class="loading-state">{{ $tc('general.loading') }}</div>
    <div v-else-if="!types.length" class="empty-state">
      {{ $tc('tournament.no_document_types') || 'No document types yet.' }}
    </div>
    <div v-else class="types-list">
      <div v-for="type in types" :key="type.id" class="type-row">
        <img :src="imageUrl(type)" class="type-thumb">
        <span class="type-name">{{ type.name }}</span>
        <div class="type-actions">
          <i class="el-icon-edit" @click="openEditModal(type)" />
          <i class="el-icon-delete" @click="removeType(type)" />
        </div>
      </div>
    </div>

    <add-document-type-modal
      v-model="modalVisible"
      :tournament-id="tournamentId"
      :seminar-id="seminarId"
      :editing-type="editingType"
      @saved="loadTypes" />
  </div>
</template>

<script>
import AddDocumentTypeModal from './AddDocumentTypeModal.vue'

export default {
  name: 'DocumentTypesPanel',
  components: { AddDocumentTypeModal },
  props: {
    tournamentId: { type: [Number, String], default: null },
    seminarId: { type: [Number, String], default: null }
  },
  data () {
    return {
      types: [],
      loading: false,
      modalVisible: false,
      editingType: null
    }
  },
  created () {
    this.loadTypes()
  },
  methods: {
    entityId () {
      return this.seminarId || this.tournamentId
    },
    imageUrl (type) {
      const baseURL = this.$config?.baseURLAPI || (this.$config?.baseURL ? this.$config.baseURL + '/api' : '') || process.env.VUE_APP_API_URL || ''
      const path = this.seminarId
        ? `seminar/${this.seminarId}/document-types/${type.id}/image`
        : `tournament/${this.tournamentId}/document-types/${type.id}/image`
      return `${baseURL}/${path}`
    },
    async loadTypes () {
      if (!this.entityId()) return
      this.loading = true
      try {
        this.types = this.seminarId
          ? await this.$store.dispatch('seminars/getDocumentTypes', this.seminarId)
          : await this.$store.dispatch('tournaments/getDocumentTypes', this.tournamentId)
      } finally {
        this.loading = false
      }
    },
    openAddModal () {
      // On the tournament/seminar CREATE route there is no id yet
      // (tournamentId/seminarId resolve to undefined until the entity is
      // first saved). Without this guard the modal would open, the admin
      // could pick an image and click Next, and only then hit a confusing
      // axios error from POST .../undefined/document-types. Same pattern
      // as the old CertificateUpload's entityId check.
      if (!this.entityId()) {
        this.$message.error(
          this.$tc('tournament.document_types_requires_saved_entity') ||
          'Save the tournament/seminar first before adding document types'
        )
        return
      }
      this.editingType = null
      this.modalVisible = true
    },
    openEditModal (type) {
      this.editingType = type
      this.modalVisible = true
    },
    async removeType (type) {
      try {
        await this.$confirm(
          this.$tc('general.confirm_delete', 0, { item: type.name }),
          this.$tc('general.warning'),
          { confirmButtonText: this.$tc('general.confirm'), cancelButtonText: this.$tc('general.cancel'), type: 'warning' }
        )
        if (this.seminarId) {
          await this.$store.dispatch('seminars/deleteDocumentType', { seminarId: this.seminarId, typeId: type.id })
        } else {
          await this.$store.dispatch('tournaments/deleteDocumentType', { tournamentId: this.tournamentId, typeId: type.id })
        }
        this.$message.success(this.$tc('general.deleted'))
        await this.loadTypes()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('Failed to delete: ' + (error.response?.data?.error || error.message))
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.document-types-panel {
  .panel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }
  .panel-title {
    font-weight: 500;
  }
  .loading-state, .empty-state {
    padding: 20px 0;
    color: #909399;
    text-align: center;
  }
  .type-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0;
    border-bottom: 1px solid #ebeef5;
    .type-thumb {
      width: 40px;
      height: 40px;
      object-fit: cover;
      border-radius: 4px;
      flex-shrink: 0;
    }
    .type-name {
      flex: 1;
    }
    .type-actions {
      i {
        cursor: pointer;
        margin-left: 10px;
        &:hover { color: #409EFF; }
      }
    }
  }
}
</style>
